import { useState, useEffect, useRef } from 'react';
import { Button } from '@ai-tutor/ui';
import { notify } from '@/lib/notify';
import {
  Plus,
  Send,
  FileText,
  Sheet,
  Presentation,
  Video,
  Music,
  ImageIcon,
  File,
  Link as LinkIcon,
  Type,
  Paperclip,
} from 'lucide-react';
import { FlowSelector } from '@/components/features/learning/content/prompt/FlowSelector';
import { ResourcePill } from '@/components/features/home/ResourcePill';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import {
  AttachSourceModal,
  isSupportedFile,
  MAX_FILE_SIZE_MB,
  type StagedSource,
} from '@/components/features/sources/attach';
import type {
  LessonSettings,
  NamedFlow,
  StagedLink,
  StagedText,
} from '@/lib/api-client';

// Sources per lesson until the plan's real cap has loaded (the server
// enforces the plan's cap either way: free 5 / Plus 20).
const DEFAULT_SOURCE_CAP = 20;
// Matches the backend `topic` cap (GenerateLessonRequest.topic max_length=2000).
const MAX_PROMPT_CHARS = 2000;

function getFileTypeInfo(mimeType: string): {
  icon: typeof FileText;
  bgClass: string;
  iconClass: string;
} {
  if (mimeType === 'application/pdf')
    return {
      icon: FileText,
      bgClass: 'bg-blue-100 dark:bg-primary/40',
      iconClass: 'text-blue-600 dark:text-primary',
    };
  if (mimeType.includes('wordprocessing') || mimeType === 'application/msword')
    return {
      icon: FileText,
      bgClass: 'bg-blue-100 dark:bg-primary/40',
      iconClass: 'text-blue-600 dark:text-primary',
    };
  if (mimeType.includes('spreadsheet') || mimeType === 'text/csv')
    return {
      icon: Sheet,
      bgClass: 'bg-green-100 dark:bg-green-900/40',
      iconClass: 'text-green-600 dark:text-green-400',
    };
  if (mimeType.includes('presentation'))
    return {
      icon: Presentation,
      bgClass: 'bg-orange-100 dark:bg-orange-900/40',
      iconClass: 'text-orange-600 dark:text-orange-400',
    };
  if (mimeType.startsWith('video/'))
    return {
      icon: Video,
      bgClass: 'bg-purple-100 dark:bg-purple-900/40',
      iconClass: 'text-purple-600 dark:text-purple-400',
    };
  if (mimeType.startsWith('audio/'))
    return {
      icon: Music,
      bgClass: 'bg-pink-100 dark:bg-pink-900/40',
      iconClass: 'text-pink-600 dark:text-pink-400',
    };
  if (mimeType.startsWith('image/'))
    return {
      icon: ImageIcon,
      bgClass: 'bg-teal-100 dark:bg-teal-900/40',
      iconClass: 'text-teal-600 dark:text-teal-400',
    };
  return {
    icon: File,
    bgClass: 'bg-muted',
    iconClass: 'text-muted-foreground',
  };
}

interface LearningPromptProps {
  onSubmit: (
    query: string,
    files?: File[],
    options?: {
      settings?: LessonSettings;
      links?: StagedLink[];
      texts?: StagedText[];
    }
  ) => void;
  className?: string;
  value?: string;
  onChange?: (value: string) => void;
  isLoading?: boolean;
  /**
   * Initial flow settings - usually the user's account-level default
   * pulled from `userLessonSettingsApi.get()`. Falls back to `core`
   * when not provided so a fresh user without a saved default still
   * sees a sensible starting choice.
   */
  initialSettings?: LessonSettings;
  /**
   * User's saved custom flow templates (FlowSelector renders them as a
   * "Saved" section in the dropdown).
   */
  savedFlows?: NamedFlow[];
  /**
   * Whether to clear the prompt + attachments after submit. Defaults to true
   * (historical behavior). The home screen passes `false` so the typed prompt
   * persists - it stays put if the submission is flagged (so the user can
   * edit) and remains visible until a successful generation redirects away.
   */
  clearOnSubmit?: boolean;
  /**
   * The plan's sources-per-lesson cap (free 5 / Plus 20). Files, links and
   * pasted text all count. Falls back to the largest plan's cap until the
   * caller has it - the server enforces the real one either way.
   */
  sourcesPerLesson?: number;
}

const DEFAULT_SETTINGS: LessonSettings = { flow: 'core' };

/**
 * Submit-button classes, hoisted out of the JSX so the component stays under
 * the `max-lines` budget and the reasoning has somewhere to live.
 *
 * `SUBMIT` mirrors the landing page's `.btn-primary` (`bg-ink` + white text:
 * maximum contrast, achromatic, accent rationed elsewhere), inverted for dark —
 * a near-white pill with near-black ink.
 *
 * The DISABLED state matters more than the enabled one, because the button is
 * disabled until you type, so it is what the screen shows by default. The
 * current style fades a tinted lavender fill to 50%, which reads as broken
 * rather than as waiting; v2 gives disabled its own quiet neutral instead.
 *
 * The light disabled fill is `--ground`, NOT `--muted`. Light `--muted` is
 * `225.8 58.9% 88.9%` (rgb 210,218,243), which against the `--card` panel is a
 * 9-point lightness drop in the near-white range - a dark grey-blue slab, and
 * since this button is disabled until you type, that slab is the page's
 * default state. Dark reads correctly because its disabled fill is a
 * translucent `white/[0.09]` lift rather than a token step; `--ground` (93.7%)
 * is the light-mode equivalent of that restraint. `--muted-foreground` on it
 * still measures 5.91:1.
 *
 * `disabled:opacity-100` is load-bearing: the shared Button's base CVA carries
 * `disabled:opacity-50`, which silently halves every colour set here — that is
 * what made the first attempt render as a ghost. Disabled is expressed by
 * COLOUR, not by fading the live style. The label alpha is pinned by contrast:
 * `/0.6` measured 4.17:1 against the button's own fill, under AA; `/0.78` is
 * 5.86:1.
 */
/**
 * Learn while a lesson is generating. The button is `disabled` then (one
 * submit at a time), but it must not wear the disabled look: that greyed it
 * out the instant it was pressed, reading as "switched off" rather than
 * "working". It keeps the primary fill instead, with a spinner and a wait
 * cursor. Appended after `SUBMIT`, so tailwind-merge lets these `disabled:`
 * fills win over SUBMIT's own.
 */
const SUBMIT_BUSY =
  'disabled:cursor-wait disabled:border-transparent disabled:bg-foreground disabled:text-background dark:disabled:border-transparent dark:disabled:bg-foreground dark:disabled:text-background';

/**
 * Learn. Light hover is `foreground/85` - the ink softening to a dark grey -
 * NOT white: a white hover under the button's pale `text-background` dropped
 * the label to ~1.2:1 contrast, all but invisible. Dark keeps its white hover,
 * where the button is already light and white is a step brighter.
 */
const SUBMIT =
  'flex items-center gap-2 px-6 py-2.5 font-semibold transition-all duration-200 enabled:bg-foreground enabled:text-background enabled:shadow-[0_2px_12px_-2px_rgb(0_0_0_/_0.5)] enabled:hover:bg-foreground/85 dark:enabled:hover:bg-white enabled:active:translate-y-px disabled:cursor-not-allowed disabled:border disabled:border-border disabled:bg-ground disabled:text-muted-foreground disabled:opacity-100 disabled:shadow-none dark:disabled:border-white/[0.14] dark:disabled:bg-white/[0.09] dark:disabled:text-foreground/[0.78]';

/** Shown, unanimated, when the user has asked for reduced motion. */
const STATIC_PLACEHOLDER = 'What do you want to learn today?';
/** Phone textarea growth cap, px: ~6 lines at 18px/1.6. */
const PHONE_MAX_TEXTAREA = 208;

export function LearningPrompt({
  onSubmit,
  className = '',
  value,
  onChange,
  isLoading = false,
  initialSettings,
  savedFlows,
  clearOnSubmit = true,
  sourcesPerLesson,
}: LearningPromptProps) {
  const [internalQuery, setInternalQuery] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [links, setLinks] = useState<StagedLink[]>([]);
  const [texts, setTexts] = useState<StagedText[]>([]);
  const [attachOpen, setAttachOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // The typewriter stops for anyone who has asked for less motion - it is a
  // loop with no end, which is exactly what that setting exists to stop.
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  // Below `sm` the composer starts short and grows with the text (see the
  // textarea); from `sm` it keeps its fixed 150px typing area.
  const isPhone = useMediaQuery('(max-width: 639.98px)');
  // Per-lesson settings forwarded to `/lessons/generate`. Initial value
  // comes from the user's account-level default (loaded by the parent);
  // falls back to `{ flow: 'core' }` when no profile preference exists.
  const [settings, setSettings] = useState<LessonSettings>(
    initialSettings ?? DEFAULT_SETTINGS
  );

  // Track whether the user has explicitly touched the picker. While
  // they haven't, we keep syncing `initialSettings` in so that:
  //   - The first async-loaded default lands (initial mount may run
  //     before the user-settings query resolves → initialSettings
  //     starts undefined, then arrives a moment later).
  //   - A default change made in Settings (or another tab) takes
  //     effect on the next Home visit even within the cache's
  //     5-minute staleTime window.
  //
  // Once the user picks a flow / edits options in this session, we
  // freeze on their choice and stop syncing - so a background
  // refetch of the saved default doesn't clobber an in-progress pick.
  const userTouchedSettingsRef = useRef(false);
  useEffect(() => {
    if (userTouchedSettingsRef.current) return;
    if (initialSettings) {
      setSettings(initialSettings);
    }
  }, [initialSettings]);

  const updateSettings = (next: LessonSettings) => {
    userTouchedSettingsRef.current = true;
    setSettings(next);
  };

  // Use controlled value if provided, otherwise use internal state
  const query = value !== undefined ? value : internalQuery;
  const setQuery = onChange || setInternalQuery;

  // Typing animation for placeholder
  useEffect(() => {
    if (query.length > 0) {
      setAnimatedPlaceholder('');
      return;
    }

    if (reduceMotion) {
      setAnimatedPlaceholder(STATIC_PLACEHOLDER);
      return;
    }

    const phrases = [
      'What do you want to learn today?',
      'Ask me about any subject...',
      'Upload files to get help with...',
      'Learn programming, math, science...',
      'Get help with your homework...',
    ];

    let currentPhraseIndex = 0;
    let currentText = '';
    let isDeleting = false;
    let timeoutId: NodeJS.Timeout;

    const typeText = () => {
      const currentPhrase = phrases[currentPhraseIndex];

      if (!isDeleting) {
        currentText = currentPhrase.substring(0, currentText.length + 1);
      } else {
        currentText = currentPhrase.substring(0, currentText.length - 1);
      }

      setAnimatedPlaceholder(currentText);

      let typingSpeed = isDeleting ? 50 : 100;

      if (!isDeleting && currentText === currentPhrase) {
        typingSpeed = 2000; // Pause at end
        isDeleting = true;
      } else if (isDeleting && currentText === '') {
        isDeleting = false;
        currentPhraseIndex = (currentPhraseIndex + 1) % phrases.length;
        typingSpeed = 500;
      }

      timeoutId = setTimeout(typeText, typingSpeed);
    };

    typeText();

    return () => clearTimeout(timeoutId);
  }, [query.length, reduceMotion]);

  // Cursor blinking animation - none under reduced motion, where the static
  // phrase reads as a plain placeholder.
  useEffect(() => {
    if (reduceMotion) {
      setShowCursor(false);
      return;
    }
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);

    return () => clearInterval(cursorInterval);
  }, [reduceMotion]);

  // Phone: the typing area starts at 128px and grows with what is typed, up to
  // 208px, then scrolls inside itself. A fixed 150px box was mostly empty on a
  // phone, and pushed the topics below the fold for a one-line prompt.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    if (!isPhone) {
      el.style.height = '';
      return;
    }
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, PHONE_MAX_TEXTAREA)}px`;
  }, [query, isPhone]);

  const totalAttached = files.length + links.length + texts.length;
  // Files, links and pasted text all count against the plan's sources cap.
  const sourceCap = sourcesPerLesson ?? DEFAULT_SOURCE_CAP;
  const slotsLeft = Math.max(0, sourceCap - totalAttached);

  // A lesson needs to know what to learn: attachments alone are not enough
  // (the request then failed with "Please enter a topic").
  const canSubmit = query.trim().length > 0;
  const handleSubmit = () => {
    // One submit at a time: Cmd/Ctrl+Enter still reaches the (enabled)
    // textarea while a lesson is generating, and the button is not the only
    // way in.
    if (!canSubmit || isLoading) return;
    onSubmit(query.trim(), files, {
      settings,
      links: links.length > 0 ? links : undefined,
      texts: texts.length > 0 ? texts : undefined,
    });
    // Callers that navigate away on success (home screen) pass
    // clearOnSubmit={false} so the prompt persists if flagged / until redirect.
    if (clearOnSubmit) {
      if (onChange) {
        onChange('');
      } else {
        setInternalQuery('');
      }
      setFiles([]);
      setLinks([]);
      setTexts([]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const maxBytes = MAX_FILE_SIZE_MB * 1024 * 1024;
    const droppedFiles = Array.from(e.dataTransfer.files).filter(file => {
      // Drag-drop previously only checked type. Enforce the same size cap the
      // attach modal uses so an oversized file can't slip through this path.
      if (!isSupportedFile(file.name)) {
        notify.error(`"${file.name}" is an unsupported file type.`);
        return false;
      }
      if (file.size > maxBytes) {
        notify.error(
          `"${file.name}" exceeds the ${MAX_FILE_SIZE_MB} MB limit.`
        );
        return false;
      }
      return true;
    });

    setFiles(prev => {
      const remaining = sourceCap - prev.length - links.length - texts.length;
      if (remaining <= 0) {
        notify.error(`Your plan allows ${sourceCap} sources per lesson`);
        return prev;
      }
      if (droppedFiles.length > remaining) {
        notify.error(
          `Only ${remaining} more source${remaining === 1 ? '' : 's'} can be added (your plan allows ${sourceCap})`
        );
      }
      return [...prev, ...droppedFiles.slice(0, remaining)];
    });
  };

  const handleAttachAdd = (source: StagedSource) => {
    if (slotsLeft <= 0) {
      notify.error(`Your plan allows ${sourceCap} sources per lesson`);
      return;
    }
    if (source.kind === 'file') {
      setFiles(prev => [...prev, source.file]);
      return;
    }
    if (source.kind === 'link') {
      setLinks(prev => [...prev, { url: source.url, name: source.name }]);
      return;
    }
    setTexts(prev => [
      ...prev,
      {
        text: source.text,
        name: source.name,
        description: source.description,
      },
    ]);
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeLink = (index: number) => {
    setLinks(prev => prev.filter((_, i) => i !== index));
  };

  const removeText = (index: number) => {
    setTexts(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className={`mx-auto w-full max-w-4xl ${className}`}>
      {/* Main Input Container
       *
       * Dark-only additions, all as `dark:` variants so light mode is byte-for-
       * byte what it was:
       *   - `dark:bg-card/60 dark:backdrop-blur-xl` - dark previously forced
       *     `dark:bg-card` OPAQUE, which sealed the panel off from the ambient
       *     bloom behind it. Letting the light through is what makes the panel
       *     sit in the room rather than on top of it.
       *   - `glass-panel` - hook for the prefers-reduced-transparency fallback.
       *   - `edge-lit` - 1px gradient outline, brightest along the top edge.
       *   - `dark:shadow-*` replaced: the old `dark:shadow-gray-900/*` were
       *     black-on-black and painted nothing. Now a real lift plus, when the
       *     user is typing, a faint lavender halo so the focused panel is the
       *     lit object on the page.
       */}
      <div
        // The typing area is 150px from `sm` (128px and growing on a phone, see
        // the textarea) - only the chrome around it is tight. Hairline rather than a 2px rule, and a
        // translucent fill so the lattice reads through the panel.
        className={`glass-panel edge-lit relative z-20 rounded-xl border bg-card/70 p-4 backdrop-blur-sm transition-all duration-300 dark:bg-card/75 dark:backdrop-blur-xl sm:p-5 ${
          isDragging
            ? `border-blue-500 from-blue-50 shadow-lg shadow-blue-500/20 dark:border-primary dark:shadow-[0_0_0_1px_hsl(258_68%_76%/0.35),0_12px_40px_-12px_hsl(258_68%_76%/0.35)]`
            : // Hairline in dark; light keeps its own border token, since a
              // white-alpha edge on a white page is invisible.
              `border-border/60 hover:border-gray-300/80 dark:border-white/[0.08] dark:hover:border-white/[0.14]`
        } ${
          query.length > 0 || files.length > 0
            ? `shadow-lg shadow-gray-200/50 dark:shadow-[0_16px_48px_-16px_rgb(0_0_0_/_0.75),0_0_28px_-14px_hsl(258_68%_76%/0.30)]`
            : 'shadow-md shadow-gray-100/30 dark:shadow-[0_12px_40px_-16px_rgb(0_0_0_/_0.65)]'
        } `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Textarea with Custom Animated Placeholder */}
        <div className='relative'>
          {/* Custom animated placeholder */}
          {query.length === 0 && (
            <div
              ref={placeholderRef}
              // Decorative: it types, deletes and retypes, so a screen reader
              // reaching it mid-word would read a fragment. The textarea's own
              // `aria-label` is the name.
              aria-hidden
              // 18px on a phone so the prompt fits one line at 390; the
              // original 20px from `sm`. Matches the textarea below.
              className='pointer-events-none absolute inset-0 flex items-start pt-0 text-[18px] leading-[1.6] text-muted-foreground sm:text-[20px]'
            >
              {animatedPlaceholder}
              <span
                className={`ml-0.5 ${showCursor ? 'opacity-100' : 'opacity-0'} transition-opacity duration-100`}
              >
                |
              </span>
            </div>
          )}

          <textarea
            value={query}
            onChange={e => setQuery(e.target.value.slice(0, MAX_PROMPT_CHARS))}
            onKeyDown={handleKeyDown}
            /* The visible prompt is the animated div above, which is
               `pointer-events-none` and so invisible to assistive tech - and
               `placeholder` is deliberately empty so it cannot double as the
               accessible name either. Without this the composer, the primary
               control on `/`, reached a screen reader as an unlabelled
               textarea. Matches the first animated phrase rather than
               inventing new wording. */
            aria-label='What do you want to learn today?'
            placeholder=''
            maxLength={MAX_PROMPT_CHARS}
            ref={textareaRef}
            className='relative z-10 min-h-32 w-full resize-none border-0 bg-transparent px-0 py-0 text-[18px] font-normal leading-[1.6] text-foreground transition-colors focus:border-0 focus:outline-none focus:ring-0 focus-visible:ring-0 sm:min-h-[150px] sm:text-[20px]'
            style={{
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
            }}
          />
          {/* Character counter - appears as the prompt nears the cap. */}
          {query.length >= MAX_PROMPT_CHARS * 0.8 && (
            <div
              className={`mt-1 text-right text-xs ${
                query.length >= MAX_PROMPT_CHARS
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-muted-foreground'
              }`}
            >
              {query.length.toLocaleString()} /{' '}
              {MAX_PROMPT_CHARS.toLocaleString()}
            </div>
          )}
        </div>

        {/* Attached resources - files, links, and pasted text shown as a
            unified horizontal pill list. */}
        {totalAttached > 0 && !canSubmit && (
          <p id='prompt-needed' className='mt-2 text-xs text-muted-foreground'>
            Say what you want to learn from{' '}
            {totalAttached === 1 ? 'this' : 'these'} to start.
          </p>
        )}
        {totalAttached > 0 && (
          <div className='mt-3 sm:mt-4'>
            {/* The count label is desktop chrome: on a phone the pills say
                what is attached on their own, and the label cost a row. */}
            <div className='mb-2 hidden items-center gap-2 sm:flex'>
              <Paperclip className='h-3.5 w-3.5 text-muted-foreground' />
              <span className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                Resources ({totalAttached})
              </span>
            </div>
            <div
              className='flex gap-2 overflow-x-auto pb-1'
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {files.map((file, index) => {
                const { icon, bgClass, iconClass } = getFileTypeInfo(file.type);
                return (
                  <ResourcePill
                    key={`file-${index}`}
                    icon={icon}
                    iconWrapClass={bgClass}
                    iconClass={iconClass}
                    label={file.name}
                    removeLabel={`Remove ${file.name}`}
                    onRemove={() => removeFile(index)}
                  />
                );
              })}
              {links.map((link, index) => (
                <ResourcePill
                  key={`link-${index}`}
                  icon={LinkIcon}
                  iconWrapClass='bg-brand-100 dark:bg-muted'
                  iconClass='text-brand-600 dark:text-primary'
                  label={link.name || link.url}
                  removeLabel={`Remove ${link.name || link.url}`}
                  onRemove={() => removeLink(index)}
                />
              ))}
              {texts.map((entry, index) => (
                <ResourcePill
                  key={`text-${index}`}
                  icon={Type}
                  iconWrapClass='bg-amber-100 dark:bg-amber-900/40'
                  iconClass='text-amber-600 dark:text-amber-400'
                  label={entry.name}
                  removeLabel={`Remove ${entry.name}`}
                  onRemove={() => removeText(index)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Bottom Actions */}
        {/* Tight gap above the toolbar and a hairline divider. Pure chrome:
            the textarea above keeps its full min-h-[150px]. */}
        {/* ONE row at every width. On a phone it fits because each control
            drops its desktop-only words: Attach becomes a round "+" (named by
            `aria-label`), Flow drops its "FLOW" caption, and Learn is a compact
            pill - about 260px in a ~326px row. Each is drawn at 40px and
            tapped at 44 (`tap-target`). */}
        <div className='mt-3 flex items-center justify-between gap-2 border-t border-border pt-3 dark:border-white/[0.07] sm:mt-4 sm:pt-3.5'>
          <div className='flex items-center gap-2 sm:gap-4'>
            {/* Attach Source Button - opens the unified modal (file/link/text).
                Made visually prominent so users discover it as the primary
                source-attach affordance. */}
            {/* Attach Source - primary secondary action. Indigo to harmonize
                with the welcome heading + the lesson-style pill's icon.
                *
                * LIGHT HOVER IS `brand-100`, NOT `brand-50`, and that is the
                * whole fix: in light `--brand-50` is `229.9 100% 97.9%` and
                * `--card` is `225.6 100% 97.8%`, which both round to
                * rgb(244,246,255). A `hover:bg-brand-50` on a `bg-card` button
                * therefore painted the exact colour it already was - measured
                * identical rest and hover fills - so light hover only moved
                * the text and border while dark got a real `primary/20` wash.
                * `brand-100` (rgb 232,236,255) is the first step that is
                * actually distinguishable from card. `brand-400` on the border
                * for the same reason: the old `brand-300` was LIGHTER than the
                * resting `--border`, so the edge lost contrast on hover. */}
            <Button
              variant='outline'
              size='sm'
              type='button'
              onClick={() => setAttachOpen(true)}
              aria-label='Attach'
              // `[&_svg]:size-2.5` is load-bearing, NOT a duplicate of the
              // `h-3 w-3` the Plus used to carry. The Button base sets
              // `[&_svg]:size-4`, and as a descendant selector that
              // out-specifies a plain `.h-3` on the icon itself — so the plus
              // measured 16px inside its own 16px circle and filled it edge to
              // edge with no padding, reading as a solid blob rather than a
              // badge. Overriding at the same arbitrary variant lets
              // tailwind-merge drop the base and the size actually applies.
              className='tap-target h-10 w-10 gap-2 rounded-full border-border bg-card p-0 text-sm font-semibold text-muted-foreground shadow-sm transition-colors duration-200 hover:border-brand-400 hover:bg-brand-100 hover:text-brand-700 dark:hover:border-primary dark:hover:bg-primary/20 dark:hover:text-primary sm:h-auto sm:w-auto sm:px-4 sm:py-2 [&_svg]:size-3.5 sm:[&_svg]:size-2.5'
            >
              {/* 14px circle around a 10px plus - the ~2px ring of fill on
                  each side is what makes this read as a badge. On a phone,
                  where the badge IS the button, 24px around 14px. */}
              <span className='flex h-6 w-6 items-center justify-center rounded-full bg-brand-600/90 text-white shadow-sm dark:bg-primary/90 sm:h-3.5 sm:w-3.5'>
                <Plus strokeWidth={2.75} />
              </span>
              <span className='hidden sm:inline'>Attach</span>
            </Button>

            <span className='hidden text-xs text-muted-foreground sm:block'>
              Files, links, or pasted text
            </span>
          </div>

          {/* Flow + Submit */}
          <div className='flex items-center gap-2 sm:gap-3'>
            <FlowSelector
              value={settings}
              onChange={updateSettings}
              disabled={isLoading}
              savedFlows={savedFlows}
              placement='bottom-end'
            />

            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || isLoading}
              aria-describedby={
                totalAttached > 0 && !canSubmit ? 'prompt-needed' : undefined
              }
              // Dark-only bloom on the ONE primary action. `dark:shadow-primary/20`
              // was a standard drop shadow tinted lavender, which on a dark page
              // is nearly invisible; this is a real halo. Keeping it to this
              // single button is what makes the accent mean "act here" - the
              // moment a second element glows, neither does.
              // A compact 40px pill on a phone (tapped at 44), the default
              // `h-9` and wider padding from `sm`.
              aria-busy={isLoading}
              className={`${SUBMIT} ${isLoading ? SUBMIT_BUSY : ''} tap-target h-10 px-4 sm:h-9 sm:px-6`}
            >
              {isLoading ? (
                <>
                  {/* `border-current`: the spinner takes the label's colour,
                      so it shows on the dark light-mode fill and the pale
                      dark-mode one alike (a fixed white vanished on the latter). */}
                  <div className='h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent' />
                  Generating...
                </>
              ) : (
                <>
                  <Send className='h-4 w-4' />
                  Learn
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <AttachSourceModal
        isOpen={attachOpen}
        onClose={() => setAttachOpen(false)}
        onAdd={handleAttachAdd}
        onRemove={(kind, idx) => {
          if (kind === 'file') removeFile(idx);
          else if (kind === 'link') removeLink(idx);
          else if (kind === 'text') removeText(idx);
        }}
        remainingFileSlots={slotsLeft}
      />
    </div>
  );
}
